import { SignupProps } from './types'
import { useTranslation } from 'react-i18next'
import { useState, useMemo } from 'react'
import { ShopifySectionComponent } from '@/types/ShopifySectionComponent'
import { Icon, useMediaQuery, Text } from '@lounge-fe/ui-kit'
import { SignupFormLayout } from './components/SignupFormLayout'
import { AnimatePresence, LayoutGroup, motion } from 'framer-motion'
import { SignUpForm } from './components/SignUpForm'
import { IntroductionFriction } from './components/IntroductionFrictionStep'
import { SuccessMessage } from './components/SuccessMessage'
import classNames from 'classnames'
import { HashRouter, Link, Navigate, Route, Routes } from 'react-router-dom'
import { PasswordForm } from './components/PasswordForm'
import { Countdown } from '@/components/Countdown'
import { twMerge } from 'tailwind-merge'

enum State {
  Friction,
  Form,
  Success,
}

const fade = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
}

const SignUpLayout: React.FC<{
  state: State
  layout?: SignupProps['layout']
  success_message?: SignupProps['success_message']
  password_form?: SignupProps['password_form']
  content?: SignupProps['content']
  button_text?: string
  event_name: string
  is_dark_mode?: boolean
  update_bloomreach_domain?: boolean
  content_alignment?: SignupProps['content_alignment']
  content_background?: SignupProps['content_background']
  content_position?: SignupProps['content_position']
  introduction_animation?: SignupProps['introduction_animation']
  is_fullscreen?: boolean
  ignore_header_height?: boolean
  setState: (state: State) => void
}> = ({
  state,
  layout = 'split',
  success_message,
  password_form,
  content,
  button_text,
  event_name,
  is_dark_mode,
  update_bloomreach_domain,
  content_alignment = 'left',
  content_background,
  content_position = 'center',
  introduction_animation,
  is_fullscreen,
  ignore_header_height,
  setState,
}) => {
  const { t } = useTranslation()

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={state}
        layout
        {...fade}
        className={classNames(
          'w-full h-full flex flex-col justify-center items-center relative',
          layout === 'split' && 'max-xl:flex-[1_1_450px] ',
          layout === 'fullscreen' &&
            (ignore_header_height
              ? 'min-h-screen h-min'
              : 'min-h-fullscreen h-min'),
        )}
      >
        {state === State.Success ? (
          <SignupFormLayout
            className={classNames(
              'w-full h-full',
              content_background !== 'transparent' && 'px-6 lg:px-0',
            )}
            layout={layout}
          >
            <SuccessMessage
              title={success_message?.title}
              subtitle={success_message?.subtitle}
              buttonText={success_message?.button_text}
              buttonUrl={success_message?.button_url}
              isDarkMode={is_dark_mode}
              password={password_form?.password}
              passwordCopyContent={password_form?.password_copy_content}
              contentAlignment={content_alignment}
              contentBackground={content_background}
            />
          </SignupFormLayout>
        ) : state === State.Form ? (
          <SignupFormLayout
            className={classNames(
              'w-full h-full',
              content_background !== 'transparent' && 'px-6 lg:px-0',
            )}
            layout={layout}
          >
            <SignUpForm
              content={content}
              buttonText={button_text}
              eventName={event_name}
              isDarkMode={is_dark_mode}
              updateBloomreachDomain={update_bloomreach_domain}
              onSuccess={() => setState(State.Success)}
              contentAlignment={content_alignment}
              contentBackground={content_background}
              contentPosition={content_position}
            />
          </SignupFormLayout>
        ) : state === State.Friction ? (
          <IntroductionFriction
            html={introduction_animation?.settings?.html}
            title={introduction_animation?.settings?.title}
            subtitle={introduction_animation?.settings?.subtitle}
            buttonText={
              introduction_animation?.settings?.button_text ??
              t('action.submit')
            }
            onNext={() => setState(State.Form)}
            isDarkMode={is_dark_mode}
          />
        ) : null}
        {password_form && (
          <div className="text-center pb-10">
            <Text
              as={Link}
              to="early-access"
              variant="body-lg"
              className="inline-flex font-regular"
            >
              <Icon.Lock className="mr-2" />
              {t('action.enter_using_password')}
            </Text>
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  )
}

export const Signup: ShopifySectionComponent<SignupProps> = ({
  data: {
    layout = 'split',
    content,
    image: image_mobile,
    video: video_mobile,
    image_desktop,
    video_desktop,
    success_message,
    introduction_animation,
    button_text,
    event_name = 'campaign_signup',
    is_dark_mode = false,
    update_bloomreach_domain = true,
    content_alignment,
    content_background,
    content_position = 'center',
    is_fullscreen = true,
    ignore_header_height = false,
    password_form,
    countdown,
  } = {},
}) => {
  const hasIntroductionAnimation = Boolean(introduction_animation)
  const isDesktop = useMediaQuery(({ screens }) => `(min-width: ${screens.xl})`)

  const [state, setState] = useState<State>(
    hasIntroductionAnimation ? State.Friction : State.Form,
  )

  const video = useMemo(() => {
    switch (state) {
      case State.Success:
        return isDesktop
          ? (success_message?.video_desktop ?? success_message?.video)
          : (success_message?.video ?? success_message?.video_desktop)
      case State.Friction:
        return isDesktop
          ? introduction_animation?.settings?.video_desktop
          : introduction_animation?.settings?.video
      default:
        return (isDesktop ? video_desktop : video_mobile) || video_mobile
    }
  }, [state, isDesktop])

  const image = useMemo(() => {
    switch (state) {
      case State.Success:
        return isDesktop
          ? (success_message?.image_desktop ??
              success_message?.image ??
              image_desktop ??
              image_mobile)
          : (success_message?.image ??
              success_message?.image_desktop ??
              image_mobile ??
              image_desktop)
      case State.Friction:
        return isDesktop
          ? (introduction_animation?.settings?.image_desktop ??
              image_desktop ??
              image_mobile)
          : (introduction_animation?.settings?.image ??
              image_mobile ??
              image_desktop)
      default:
        return (isDesktop ? image_desktop : image_mobile) ?? image_mobile
    }
  }, [state, isDesktop])

  const [hasAnimationCompleted, setHasAnimationCompleted] = useState<boolean>(
    !hasIntroductionAnimation || isDesktop,
  )

  const shouldRenderMedia =
    state < State.Form ||
    layout === 'fullscreen' ||
    isDesktop ||
    !hasIntroductionAnimation

  const withRouter = Boolean(password_form)

  const signUpLayoutElement = (
    <SignUpLayout
      state={state}
      layout={layout}
      success_message={success_message}
      password_form={password_form}
      content={content}
      button_text={button_text}
      event_name={event_name}
      is_dark_mode={is_dark_mode}
      update_bloomreach_domain={update_bloomreach_domain}
      content_alignment={content_alignment}
      content_background={content_background}
      content_position={content_position}
      introduction_animation={introduction_animation}
      is_fullscreen={is_fullscreen}
      ignore_header_height={ignore_header_height}
      setState={setState}
    />
  )

  return (
    <LayoutGroup id="signup">
      <AnimatePresence mode="wait" initial={false}>
        <SignupFormLayout
          layout={layout}
          className={classNames(
            is_dark_mode && 'dark dark:[&>svg]text-white bg-black',
            is_fullscreen &&
              (ignore_header_height
                ? 'min-h-screen h-min'
                : 'min-h-fullscreen h-min'),
          )}
        >
          {(image || video) && (
            <AnimatePresence
              initial={false}
              onExitComplete={() => setHasAnimationCompleted(true)}
            >
              {shouldRenderMedia && (
                <motion.div
                  {...(hasAnimationCompleted ? {} : fade)}
                  className={twMerge(
                    'relative h-full w-full',
                    layout === 'split' && 'flex',
                    layout === 'fullscreen' && 'absolute inset-0',
                  )}
                >
                  {countdown?.endTime && countdown?.postCountdownText && (
                    <Countdown
                      data={{
                        endTime: countdown?.endTime,
                        postCountdownText: countdown?.postCountdownText,
                        className:
                          'px-6 bottom-6 z-50 max-w-[450px] w-full md:bottom-20 text-white left-1/2 -translate-x-1/2 absolute',
                      }}
                    />
                  )}
                  <SignupFormLayout.Media
                    image={image}
                    video={video}
                    className={classNames(
                      layout === 'split' && 'max-lg:max-h-[450px]',
                    )}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          )}
          {withRouter ? (
            <HashRouter key={location.hash}>
              <Routes>
                <Route key="/" path="/" index element={signUpLayoutElement} />
                <Route
                  path="/early-access"
                  element={
                    <div
                      className={classNames(
                        'w-full h-full flex flex-col justify-center items-center relative',
                      )}
                    >
                      <PasswordForm
                        title={password_form?.title}
                        error={password_form?.error}
                        subtitle={password_form?.subtitle}
                        content={password_form?.content}
                      />
                    </div>
                  }
                />
                <Route path="/*" element={<Navigate to="/" replace />} />
              </Routes>
            </HashRouter>
          ) : (
            signUpLayoutElement
          )}
        </SignupFormLayout>
      </AnimatePresence>
    </LayoutGroup>
  )
}

export default Signup
